import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from "@mui/material";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../../widgets/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../widgets/Navbars/DashboardNavbar";
import { AppAutocomplete, AppButton, AppTextField } from "../../../components/form";
import { AppFormField, AppFormGrid, PageListContainer } from "../../../components/Page";
import { GRID_FULL } from "../../../lib/ui/formLayout";
import DateRangeHeader, { useDateRangeHeader } from "../../../components/DateRangeHeader";
import WidgetOutputView from "../../../components/WidgetOutputView";
import WidgetParametersEditor from "../../../components/WidgetParametersEditor";
import WidgetQuerySourcePanel from "../../../components/WidgetQuerySourcePanel";
import WidgetSourceDateRanges from "../../../components/WidgetSourceDateRanges";
import JsonCodeEditor from "../../../components/JsonCodeEditor";
import ChartEditorPanel from "../../../components/ChartEditorPanel";
import WidgetVisualEditor from "../../../components/WidgetVisualEditor";
import WidgetVersionHistory from "../../../components/WidgetVersionHistory";
import {
  buildWidgetChartMapping,
  mappingToJson,
  parseWidgetChartMapping,
} from "../../../utils/chartMappingBuilder";
import { widgetService } from "../../../services/widgetService";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import { useDebouncedEffect } from "../../../hooks/useDebouncedEffect";
import type { Widget, WidgetDataSource, WidgetExecuteResponse, WidgetOutput } from "../../../types/widget";
import {
  applySourcesToDataSource,
  buildWidgetExecuteRequest,
  normalizeWidgetSources,
  syncSourceDateRanges,
  type SourceDateRange,
} from "../../../utils/widgetDataSources";
import { dashboardTimezone } from "../../../utils/dashboardDateDefaults";
import { analyzeOpenSearchTableResponse, type OpenSearchTableAnalysis } from "../../../utils/openSearchTableAnalysis";
import WidgetEditStudioLayout, { WidgetEditSectionCard } from "./widgetEdit/WidgetEditStudioLayout";
import {
  DEFAULT_DSL,
  DEFAULT_MAPPING,
  DEFAULT_PARAMETERS,
  DEFAULT_TABLE_DSL,
  EMPTY_TABLE_MAPPING,
  OUTPUT_FORMAT_VALUES,
  emptyWidget,
  ensureDashboardDateParameters,
  isValidJson,
  parseJsonField,
} from "./widgetEdit/constants";
import WidgetAdvancedCodePanel from "./widgetEdit/WidgetAdvancedCodePanel";
import WidgetDocsPanel from "./widgetEdit/WidgetDocsPanel";
import type { WidgetEditSection, WidgetEditorMode } from "./widgetEdit/types";
import WidgetRowBindingEditor, {
  type WidgetRowBinding,
} from "../../../components/WidgetRowBindingEditor";

type Mode = "create" | "edit";

type WidgetEditPageProps = {
  mode: Mode;
  embedded?: boolean;
  widgetId?: string;
  onClose?: () => void;
  onSaved?: () => void;
};

export default function WidgetEditPage({
  mode,
  embedded = false,
  widgetId,
  onClose,
  onSaved,
}: WidgetEditPageProps) {
  const { id: routeId } = useParams();
  const id = widgetId ?? routeId;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const goBack = () => {
    if (onClose) onClose();
    else navigate("/widgets");
  };
  const date = useDateRangeHeader();
  const [widget, setWidget] = useState<Widget>(emptyWidget);
  const [dslText, setDslText] = useState(DEFAULT_DSL);
  const [queryBodyText, setQueryBodyText] = useState("SELECT 1 AS id");
  const [mappingText, setMappingText] = useState(DEFAULT_MAPPING);
  const [mappingHintText, setMappingHintText] = useState("{}");
  const [sources, setSources] = useState(emptyWidget().dataSource!.sources!);
  const [sourceDateRanges, setSourceDateRanges] = useState<SourceDateRange[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [previewResult, setPreviewResult] = useState<WidgetExecuteResponse | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  const [activeSection, setActiveSection] = useState<WidgetEditSection>("overview");
  const [editorMode, setEditorMode] = useState<WidgetEditorMode>("visual");
  const [autoPreview, setAutoPreview] = useState(true);
  const [tableAnalysis, setTableAnalysis] = useState<OpenSearchTableAnalysis | null>(null);
  const [probeLoading, setProbeLoading] = useState(false);
  const [probeError, setProbeError] = useState<string | null>(null);
  const [dslPlaygroundLoading, setDslPlaygroundLoading] = useState(false);
  const [dslPlaygroundError, setDslPlaygroundError] = useState<string | null>(null);
  const [dslPlaygroundResult, setDslPlaygroundResult] = useState<string | null>(null);

  const pageTitle = mode === "create" ? t("widget.create") : t("widget.edit");
  const primaryIndex = useMemo(() => sources[0]?.index || "", [sources]);
  const primaryTimeField = useMemo(
    () => sources[0]?.timeField || widget.dataSource?.timeField || "",
    [sources, widget.dataSource?.timeField]
  );
  const multiSource = sources.length > 1;
  const adaptorSource = (widget.dataSource?.sourceType ?? "OPENSEARCH") === "ADAPTOR";

  const dslJsonError = useMemo(() => !isValidJson(dslText), [dslText]);
  const mappingJsonError = useMemo(() => !isValidJson(mappingText), [mappingText]);
  const mappingHintJsonError = useMemo(() => !isValidJson(mappingHintText), [mappingHintText]);

  const chartMapping = useMemo(() => {
    try {
      return parseWidgetChartMapping(JSON.parse(mappingText));
    } catch {
      return buildWidgetChartMapping("bar", "vertical", widget.name);
    }
  }, [mappingText, widget.name]);

  const outputFormats = useMemo(
    () =>
      OUTPUT_FORMAT_VALUES.map((value) => ({
        value,
        label: t(`widget.format.${value}`),
      })),
    [t]
  );

  const outputFormat =
    outputFormats.find((f) => f.value === (widget.output?.format || "echarts")) ?? outputFormats[0];

  const onChartMappingChange = useCallback((next: ReturnType<typeof parseWidgetChartMapping>) => {
    setMappingText(mappingToJson(next));
    setPreviewResult(null);
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const w = await widgetService.get(id);
      setWidget(w);
      const loadedSources = normalizeWidgetSources(w.dataSource);
      setSources(loadedSources.length ? loadedSources : []);
      setDslText(JSON.stringify(w.dataSource?.dslTemplate ?? {}, null, 2));
      setQueryBodyText(w.dataSource?.queryBody ?? "SELECT 1 AS id");
      setMappingText(JSON.stringify(w.output?.mapping ?? {}, null, 2));
      setMappingHintText(JSON.stringify(w.dataSource?.mappingHint ?? {}, null, 2));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (mode === "edit") {
      load().catch(console.error);
    }
  }, [load, mode]);

  useEffect(() => {
    setSourceDateRanges((previous) => syncSourceDateRanges(previous, sources, date.range));
  }, [sources, date.range.from, date.range.to]);

  const buildDataSource = useCallback((): WidgetDataSource => {
    const sourceType = widget.dataSource?.sourceType ?? "OPENSEARCH";
    const parameters = ensureDashboardDateParameters(widget.dataSource?.parameters);
    if (sourceType === "ADAPTOR") {
      return {
        ...widget.dataSource,
        sourceType: "ADAPTOR",
        adaptorId: widget.dataSource?.adaptorId,
        queryLanguage: widget.dataSource?.queryLanguage ?? "SQL",
        queryBody: queryBodyText.trim(),
        parameters,
      };
    }
    return applySourcesToDataSource(
      {
        ...widget.dataSource,
        sourceType: "OPENSEARCH",
        dslTemplate: parseJsonField(dslText, t("widget.dslTemplate")),
        mappingHint: parseJsonField(mappingHintText, t("widget.mappingHint")),
        parameters,
      },
      sources
    );
  }, [widget, sources, dslText, mappingHintText, queryBodyText, t]);

  const rowBinding = useMemo((): WidgetRowBinding => {
    try {
      const mapping = JSON.parse(mappingText) as { rowBinding?: WidgetRowBinding };
      return mapping.rowBinding ?? {};
    } catch {
      return {};
    }
  }, [mappingText]);

  const adaptorFieldOptions = useMemo(() => {
    const fields = tableAnalysis?.fieldsBySource?.["hits._root"] ?? [];
    return fields.map((f) => f.field || f.path).filter(Boolean);
  }, [tableAnalysis]);

  const updateRowBinding = useCallback(
    (next: WidgetRowBinding) => {
      try {
        const mapping = JSON.parse(mappingText) as Record<string, unknown>;
        const cleaned: WidgetRowBinding = {};
        if (next.categoryField?.trim()) cleaned.categoryField = next.categoryField.trim();
        if (next.valueField?.trim()) cleaned.valueField = next.valueField.trim();
        if (next.seriesField?.trim()) cleaned.seriesField = next.seriesField.trim();
        if (cleaned.categoryField) {
          mapping.rowBinding = cleaned;
        } else {
          delete mapping.rowBinding;
        }
        setMappingText(JSON.stringify(mapping, null, 2));
        setPreviewResult(null);
      } catch {
        // ignore invalid mapping JSON while editing
      }
    },
    [mappingText]
  );

  const hasAdaptorChartBindingWarning = useMemo(
    () =>
      Boolean(
        previewResult?.warnings?.some(
          (w) => w?.startsWith("ADAPTOR_CHART_BINDING") || w?.startsWith("CHART_NO_DATA")
        )
      ),
    [previewResult]
  );

  const buildPayload = useCallback((): Widget => {
    return {
      ...widget,
      dataSource: buildDataSource(),
      output: {
        format: (widget.output?.format || "echarts") as WidgetOutput["format"],
        mapping: parseJsonField(mappingText, t("widget.outputMapping")),
      },
    };
  }, [widget, buildDataSource, mappingText, t]);

  const buildProbePayload = useCallback((): Widget | null => {
    if (adaptorSource) {
      if (!widget.dataSource?.adaptorId || !queryBodyText.trim()) {
        return null;
      }
      try {
        return {
          ...widget,
          dataSource: buildDataSource(),
          output: {
            format: (widget.output?.format || "echarts") as WidgetOutput["format"],
            mapping: { columns: [] },
          },
        };
      } catch {
        return null;
      }
    }
    if (dslJsonError || !sources.some((source) => source.index?.trim())) {
      return null;
    }
    try {
      return {
        ...widget,
        dataSource: buildDataSource(),
        output: {
          format: (widget.output?.format || "echarts") as WidgetOutput["format"],
          mapping: { columns: [] },
        },
      };
    } catch {
      return null;
    }
  }, [widget, sources, dslJsonError, adaptorSource, queryBodyText, buildDataSource]);

  const runDslProbe = useCallback(async () => {
    const payload = buildProbePayload();
    if (!payload) {
      setTableAnalysis(null);
      setProbeError(null);
      return;
    }
    setProbeLoading(true);
    setProbeError(null);
    try {
      const executeBody = buildWidgetExecuteRequest(sources, sourceDateRanges, date.range, dashboardTimezone());
      const res = await widgetService.probeDsl(payload, executeBody);
      if (!res.valid) {
        setProbeError(res.errors.join("; "));
        setTableAnalysis(null);
        return;
      }
      setTableAnalysis(analyzeOpenSearchTableResponse(res.openSearchResponse));
    } catch (e: unknown) {
      setProbeError(e instanceof Error ? e.message : String(e));
      setTableAnalysis(null);
    } finally {
      setProbeLoading(false);
    }
  }, [buildProbePayload, sources, sourceDateRanges, date.range]);

  const runDslPlayground = useCallback(async () => {
    const payload = buildProbePayload();
    if (!payload) {
      setDslPlaygroundError(
        adaptorSource ? t("widget.queryPlaygroundNeedAdaptor") : t("widget.dslPlaygroundNeedSource")
      );
      setDslPlaygroundResult(null);
      return;
    }
    if (!adaptorSource && dslJsonError) {
      setDslPlaygroundError(t("widget.invalidJson"));
      setDslPlaygroundResult(null);
      return;
    }
    setDslPlaygroundLoading(true);
    setDslPlaygroundError(null);
    try {
      const executeBody = buildWidgetExecuteRequest(sources, sourceDateRanges, date.range, dashboardTimezone());
      const res = await widgetService.probeDsl(payload, executeBody);
      if (!res.valid) {
        setDslPlaygroundError(res.errors.join("; "));
        setDslPlaygroundResult(null);
        return;
      }
      setDslPlaygroundResult(JSON.stringify(res.openSearchResponse ?? {}, null, 2));
    } catch (e: unknown) {
      setDslPlaygroundError(e instanceof Error ? e.message : String(e));
      setDslPlaygroundResult(null);
    } finally {
      setDslPlaygroundLoading(false);
    }
  }, [buildProbePayload, sources, sourceDateRanges, date.range, dslJsonError, adaptorSource, t]);

  const onValidate = async () => {
    try {
      setErrors([]);
      const payload = buildPayload();
      const res = await widgetService.validate(payload);
      if (!res.valid) {
        setErrors(res.errors);
        return;
      }
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: { color: "success", icon: "check", title: t("widget.validate"), content: "" },
        })
      );
    } catch (e: unknown) {
      setErrors([e instanceof Error ? e.message : String(e)]);
    }
  };

  const onPreview = useCallback(
    async (options?: { navigate?: boolean }) => {
      try {
        setPreviewing(true);
        setErrors([]);
        const payload = buildPayload();
        const executeBody = buildWidgetExecuteRequest(sources, sourceDateRanges, date.range, dashboardTimezone());
        const res = await widgetService.preview(payload, executeBody);
        if (!res.valid && !res.sampleResult) {
          setErrors(res.errors?.length ? res.errors : [res.error || t("widget.error.generic")]);
          setPreviewResult(null);
          return;
        }
        if (!res.valid && res.errors?.length) {
          setErrors(res.errors);
        }
        setPreviewResult({
          format: res.format || payload.output?.format || "echarts",
          payload: res.sampleResult,
          error: res.error,
          warnings: res.warnings,
        });
        if (options?.navigate !== false) {
          setActiveSection("preview");
        }
      } catch (e: unknown) {
        setErrors([e instanceof Error ? e.message : String(e)]);
      } finally {
        setPreviewing(false);
      }
    },
    [buildPayload, sources, sourceDateRanges, date.range, t]
  );

  useDebouncedEffect(
    () => {
      runDslProbe().catch(console.error);
    },
    [
      widget.output?.format,
      widget.dataSource?.adaptorId,
      sources,
      sourceDateRanges,
      dslText,
      queryBodyText,
      adaptorSource,
      date.range.from,
      date.range.to,
      dslJsonError,
    ],
    800,
    (["table", "echarts"] as const).includes((widget.output?.format || "echarts") as "table" | "echarts")
  );

  useDebouncedEffect(
    () => {
      if (!autoPreview) return;
      if (mappingJsonError) return;
      if (adaptorSource) {
        if (!widget.dataSource?.adaptorId || !queryBodyText.trim()) return;
      } else if (dslJsonError) {
        return;
      }
      if (!widget.name?.trim()) return;
      onPreview({ navigate: false });
    },
    [
      autoPreview,
      widget,
      sources,
      sourceDateRanges,
      dslText,
      queryBodyText,
      adaptorSource,
      mappingText,
      mappingHintText,
      date.range.from,
      date.range.to,
      dslJsonError,
      mappingJsonError,
      onPreview,
    ],
    500,
    autoPreview
  );

  const onSave = async () => {
    try {
      setSaving(true);
      setErrors([]);
      const payload = buildPayload();
      const saved =
        mode === "edit" && id
          ? await widgetService.update(id, payload)
          : await widgetService.create(payload);
      if (embedded) {
        onSaved?.();
        onClose?.();
      } else {
        navigate("/widgets", { replace: true });
      }
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: saved.name || t("widget.title"),
            content: saved.status === "PENDING_INDEX_APPROVAL" ? t("widget.pendingApproval") : "",
          },
        })
      );
    } catch (e: unknown) {
      setErrors([e instanceof Error ? e.message : String(e)]);
    } finally {
      setSaving(false);
    }
  };

  const statusChip =
    widget.status === "PENDING_INDEX_APPROVAL" ? (
      <Chip size="small" color="warning" label={t("widget.pendingApprovalShort")} sx={custom.pages.shared.chipPendingApproval} />
    ) : null;

  const previewSectionActions = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {t("visualEditor.autoPreviewOn")}
        </Typography>
        <Switch size="small" checked={autoPreview} onChange={(e) => setAutoPreview(e.target.checked)} />
      </Stack>
      <AppButton
        size="small"
        variant="outlined"
        startIcon={previewing ? <CircularProgress size={14} /> : <PlayArrowOutlinedIcon />}
        onClick={() => onPreview({ navigate: false })}
        disabled={previewing}
      >
        {previewing ? t("widget.previewing") : t("widget.preview")}
      </AppButton>
    </Stack>
  );

  const previewContent = (
    <Stack
      spacing={1.25}
      sx={custom.pages.widgets.editPreviewRoot}
    >
      <Box sx={custom.pages.widgets.editPreviewHeader}>
        {multiSource ? (
          <WidgetSourceDateRanges ranges={sourceDateRanges} onChange={setSourceDateRanges} disabled={previewing} />
        ) : (
          <DateRangeHeader
            range={date.range}
            preset={date.preset}
            onPresetChange={date.setPreset}
            customFrom={date.customFrom}
            customTo={date.customTo}
            onCustomFromChange={date.setCustomFrom}
            onCustomToChange={date.setCustomTo}
            onApplyCustom={date.applyCustom}
            label={date.label}
            onRefresh={() => onPreview({ navigate: false })}
            executing={previewing}
          />
        )}
      </Box>

      {previewResult ? (
        <Box sx={custom.pages.widgets.editPreviewPayload}>
          <WidgetOutputView result={previewResult} loading={previewing} />
        </Box>
      ) : (
        <Box
          sx={custom.pages.widgets.editPreviewPlaceholder}
        >
          <Typography variant="body2" color="text.secondary">
            {previewing ? t("widget.previewing") : t("widget.previewEmpty")}
          </Typography>
        </Box>
      )}
    </Stack>
  );

  const sectionContent = (() => {
    switch (activeSection) {
      case "overview":
        return (
          <WidgetEditSectionCard
            title={t("widget.sectionGeneral")}
            description={t("widget.sectionGeneralHint")}
          >
            {widget.status === "PENDING_INDEX_APPROVAL" && (
              <Alert severity="warning" sx={custom.pages.widgets.editPendingAlert}>
                {t("widget.pendingApproval")}
              </Alert>
            )}
            <AppFormGrid spacing="compact">
              <AppFormField size={{ xs: 12, md: 7 }}>
                <AppTextField
                  label={t("widget.name")}
                  value={widget.name || ""}
                  onChange={(e) => setWidget((w) => ({ ...w, name: e.target.value }))}
                  autoFocus
                />
              </AppFormField>
              <AppFormField size={{ xs: 12, md: 5 }}>
                <AppAutocomplete<(typeof outputFormats)[number]>
                  label={t("widget.outputFormat")}
                  options={outputFormats}
                  value={outputFormat}
                  onChange={(_, opt) => {
                    if (!opt) return;
                    if (opt.value === "table") {
                      setMappingText(EMPTY_TABLE_MAPPING);
                      if (!adaptorSource) {
                        try {
                          const dsl = JSON.parse(dslText) as { size?: number };
                          if (dsl.size === 0 || dsl.size == null) {
                            setDslText(DEFAULT_TABLE_DSL);
                          }
                        } catch {
                          setDslText(DEFAULT_TABLE_DSL);
                        }
                      }
                      setTableAnalysis(null);
                    }
                    setWidget((w) => ({
                      ...w,
                      output: { ...w.output, format: opt.value },
                    }));
                  }}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  disableClearable
                />
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("widget.description")}
                  value={widget.description || ""}
                  onChange={(e) => setWidget((w) => ({ ...w, description: e.target.value }))}
                  multiline
                  minRows={2}
                />
              </AppFormField>
            </AppFormGrid>
          </WidgetEditSectionCard>
        );

      case "data":
        return (
          <WidgetEditSectionCard
            title={t("widget.sectionDataSource")}
            description={
              adaptorSource ? t("widget.sectionDataSourceHintAdaptor") : t("widget.sectionDataSourceHint")
            }
          >
            <WidgetQuerySourcePanel
              dataSource={widget.dataSource}
              sources={sources}
              onDataSourceChange={(patch) =>
                setWidget((w) => ({ ...w, dataSource: { ...w.dataSource, ...patch } }))
              }
              onSourcesChange={setSources}
              dslText={dslText}
              onDslTextChange={setDslText}
              dslJsonError={dslJsonError}
              queryBodyText={queryBodyText}
              onQueryBodyTextChange={setQueryBodyText}
            />
          </WidgetEditSectionCard>
        );

      case "presentation":
        return (
          <WidgetEditSectionCard
            title={t("visualEditor.title")}
            description={t("visualEditor.subtitle")}
            actions={
              <ToggleButtonGroup
                size="small"
                exclusive
                value={editorMode}
                onChange={(_, v: WidgetEditorMode | null) => v && setEditorMode(v)}
              >
                <ToggleButton value="visual" sx={custom.pages.widgets.editModeToggle}>
                  <DashboardCustomizeOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                  {t("visualEditor.modeVisual")}
                </ToggleButton>
                <ToggleButton value="advanced" sx={custom.pages.widgets.editModeToggle}>
                  <DataObjectOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                  {t("visualEditor.modeAdvanced")}
                </ToggleButton>
              </ToggleButtonGroup>
            }
          >
            {editorMode === "visual" ? (
              <Stack spacing={2}>
                <WidgetVisualEditor
                  outputFormat={(widget.output?.format || "echarts") as NonNullable<WidgetOutput["format"]>}
                  widgetName={widget.name}
                  primaryIndex={primaryIndex}
                  timeField={primaryTimeField}
                  mappingText={mappingText}
                  onMappingTextChange={(text) => {
                    setMappingText(text);
                    setPreviewResult(null);
                  }}
                  tableAnalysis={tableAnalysis}
                  probeLoading={probeLoading}
                  probeError={probeError}
                  adaptorSource={adaptorSource}
                  onRefreshProbe={() => {
                    runDslProbe().catch(console.error);
                  }}
                  onTimeFieldChange={
                    adaptorSource
                      ? undefined
                      : (timeField) =>
                          setSources((prev) =>
                            prev.length
                              ? prev.map((source, index) =>
                                  index === 0 ? { ...source, timeField } : source
                                )
                              : prev
                          )
                  }
                />
                {adaptorSource && (widget.output?.format || "echarts") === "echarts" ? (
                  <WidgetRowBindingEditor
                    binding={rowBinding}
                    fieldOptions={adaptorFieldOptions}
                    onChange={updateRowBinding}
                    showMissingWarning={hasAdaptorChartBindingWarning && !rowBinding.categoryField}
                  />
                ) : null}
              </Stack>
            ) : (
              <AppFormGrid>
                {(widget.output?.format || "echarts") === "echarts" && (
                  <AppFormField size={GRID_FULL}>
                    <ChartEditorPanel mapping={chartMapping} onChange={onChartMappingChange} />
                  </AppFormField>
                )}
                {adaptorSource && (widget.output?.format || "echarts") === "echarts" ? (
                  <AppFormField size={GRID_FULL}>
                    <WidgetRowBindingEditor
                      binding={rowBinding}
                      fieldOptions={adaptorFieldOptions}
                      onChange={updateRowBinding}
                      showMissingWarning={hasAdaptorChartBindingWarning && !rowBinding.categoryField}
                    />
                  </AppFormField>
                ) : null}
                <AppFormField size={GRID_FULL}>
                  <JsonCodeEditor
                    label={t("widget.outputMapping")}
                    value={mappingText}
                    onChange={(v) => {
                      setMappingText(v);
                      setPreviewResult(null);
                    }}
                    error={mappingJsonError}
                    helperText={mappingJsonError ? t("widget.invalidJson") : undefined}
                    height={320}
                  />
                </AppFormField>
              </AppFormGrid>
            )}
          </WidgetEditSectionCard>
        );

      case "preview":
        return (
          <WidgetEditSectionCard
            title={t("widgetEditStudio.livePreview")}
            description={t("dashboard.widgetEditPreviewHint")}
            actions={previewSectionActions}
            noPadding
            fillHeight
          >
            {previewContent}
          </WidgetEditSectionCard>
        );

      case "parameters":
        return (
          <WidgetEditSectionCard title={t("widget.parameters")}>
            <WidgetParametersEditor
              parameters={widget.dataSource?.parameters || DEFAULT_PARAMETERS}
              adaptorSource={adaptorSource}
              onChange={(parameters) =>
                setWidget((w) => ({
                  ...w,
                  dataSource: {
                    ...w.dataSource,
                    parameters: ensureDashboardDateParameters(parameters),
                  },
                }))
              }
            />
          </WidgetEditSectionCard>
        );

      case "code":
        return (
          <WidgetEditSectionCard
            title={t("widget.sectionAdvanced")}
            description={
              adaptorSource ? t("widget.sectionAdvancedHintAdaptor") : t("widget.sectionAdvancedHint")
            }
          >
            <WidgetAdvancedCodePanel
              sourceType={adaptorSource ? "ADAPTOR" : "OPENSEARCH"}
              queryLanguage={widget.dataSource?.queryLanguage}
              mappingHintText={mappingHintText}
              mappingHintJsonError={mappingHintJsonError}
              onMappingHintChange={setMappingHintText}
              dslText={dslText}
              dslJsonError={dslJsonError}
              onDslChange={(value) => {
                setDslText(value);
                setTableAnalysis(null);
                setDslPlaygroundResult(null);
              }}
              queryBodyText={queryBodyText}
              onQueryBodyChange={(value) => {
                setQueryBodyText(value);
                setTableAnalysis(null);
                setDslPlaygroundResult(null);
              }}
              primaryIndex={primaryIndex}
              adaptorReady={Boolean(widget.dataSource?.adaptorId)}
              onExecuteQuery={runDslPlayground}
              executeLoading={dslPlaygroundLoading}
              executeError={dslPlaygroundError}
              executeResult={dslPlaygroundResult}
            />
          </WidgetEditSectionCard>
        );

      case "docs":
        return (
          <WidgetEditSectionCard title={t("widgetDocs.title")} description={t("widgetDocs.subtitle")}>
            <WidgetDocsPanel />
          </WidgetEditSectionCard>
        );

      case "history":
        return mode === "edit" && id ? (
          <WidgetEditSectionCard title={t("widgetVersion.title")} description={t("widgetVersion.subtitle")}>
            <WidgetVersionHistory widgetId={id} onRestored={load} />
          </WidgetEditSectionCard>
        ) : null;

      default:
        return null;
    }
  })();

  const body = (
    <PageListContainer surface="plain">
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={custom.pages.shared.loadingStackPy10} spacing={2}>
          <CircularProgress size={28} />
          <Typography color="text.secondary">{t("common.loading")}</Typography>
        </Stack>
      ) : (
        <WidgetEditStudioLayout
          mode={mode}
          title={pageTitle}
          widgetName={widget.name}
          outputFormatLabel={outputFormat.label}
          statusChip={statusChip}
          errors={errors}
          saving={saving}
          previewing={previewing}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          showHistoryNav={mode === "edit" && Boolean(id)}
          onBack={goBack}
          onValidate={onValidate}
          onPreview={() => onPreview()}
          onSave={onSave}
          content={sectionContent}
        />
      )}
    </PageListContainer>
  );

  if (embedded) return body;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {body}
    </DashboardLayout>
  );
}
